// Partial-refund reconciliation for the Online Orders "Completed" tab.
//
// Phase 5: this logic previously lived *inside* the OnlineOrders.vue
// `filteredOrders` computed, which made the computed impure — every time the
// list re-evaluated it mutated store objects (stamping `isPartialRefund` on
// items, bumping `total_sum`, and flagging `isFullyRefund`). The computed is
// now pure and this reconciliation runs from a `filteredOrders` watcher
// instead. The behavior (and the in-place mutation of the store objects so the
// flags carry over when an order becomes `currentOrder`) is preserved exactly.
//
// The function is intentionally written to mutate the order objects it is
// given (the same store references the list renders), mirroring the original
// implementation: the flags must land on the objects that later become the
// previewed `currentOrder` so the sidebar's "Fully Refunded" button and the
// per-item partial-refund dot reflect the reconciled state.

import { ORDER_STATUS } from "@/helpers/order/status";

/**
 * Reconciles partial/full refunds for a list of completed orders against the
 * known refunded orders, stamping display flags in place.
 *
 * For each refunded order that references one of the completed orders, the
 * matching reference item is flagged `isPartialRefund` and (once per refund
 * item, guarded by `void_item`) its refunded grand_total is added back into the
 * reference order's running `total_sum`. A completed order is then flagged
 * `isFullyRefund` when its remaining `total_sum` has collapsed to just tax (or
 * to ~zero).
 *
 * @param {Array<Object>} completedOrders The visible completed orders (mutated).
 * @param {Array<Object>} refundedOrders  The known refunded orders (their
 *   `items[].void_item` flag is mutated as a one-shot guard).
 * @returns {Array<Object>} The same `completedOrders` array, for chaining.
 */
export function reconcilePartialRefunds (completedOrders, refundedOrders) {
  const orders = completedOrders || [];
  const refunds = refundedOrders || [];

  refunds.forEach(order => {
    if (order.reference_order !== null && order.status === ORDER_STATUS.REFUNDED) {
      const selectedOrder = orders.find(selectedOrder => {
        if (selectedOrder.id === order.reference_order) {
          return selectedOrder;
        }
      });
      const selectedOrderIndex = orders.findIndex(selectedOrder => selectedOrder.id === order.reference_order);
      if (selectedOrder) {
        if (order.items.length > 0) {
          order.items.forEach(item => {
            let itemIndex = -1;
            selectedOrder.items.findIndex((selectedItem, index) => {
              if (selectedItem.id === item.order_refund_item) {
                itemIndex = index;
                return index;
              }
            });
            if (itemIndex > -1) {
              if (item.void_status === 0 && (selectedOrder.items[itemIndex].void_status === 0 || selectedOrder.items[itemIndex].void_status === null)) {
                // as orders are fetched after every 2 sec thus setting isPartialRefund is set to true
                selectedOrder.items[itemIndex].isPartialRefund = true;

                // all the values are already in negative thus add operation will be perform
                if (item.void_item === false) {
                  item.void_item = true;
                  selectedOrder.items[itemIndex].isPartialRefund = true;
                  selectedOrder.total_sum += parseFloat((item.grand_total).toFixed(2));
                  orders[selectedOrderIndex] = selectedOrder;
                }
              }
            }
          });
        }
      }
    }
  });

  orders.forEach((selectedOrder, selectedOrderIndex) => {
    if (selectedOrder?.status === ORDER_STATUS.COMPLETED && selectedOrder?.total_sum?.toFixed(2) === selectedOrder?.tax_total?.toFixed(2) || selectedOrder?.total_sum <= 0.01) {
      selectedOrder.isFullyRefund = true;
      orders[selectedOrderIndex] = selectedOrder;
    }
  });

  return orders;
}
